/**
 * drafts.js — Small utility to persist work-in-progress form data in localStorage.
 *
 * Used to recover a partially-filled "Nuovo preventivo" / "Nuova fattura" when
 * the user accidentally (or intentionally) closes the form before submitting.
 *
 * One named slot per form (e.g. 'preventivo', 'fattura'). The data is namespaced
 * by user-id when available so multiple users sharing a device don't collide.
 */

const PREFIX = 'cantiereai:draft';

function userScope() {
    try {
        const raw = localStorage.getItem('user');
        if (!raw) return 'anon';
        const u = JSON.parse(raw);
        return u?.id || u?.email || 'anon';
    } catch {
        return 'anon';
    }
}

function keyFor(slot) {
    return `${PREFIX}:${slot}:${userScope()}`;
}

/**
 * Returns true when the draft has at least one meaningful, user-provided field.
 * Used to avoid persisting "blank" forms.
 */
function isMeaningful(draft, requiredFields = []) {
    if (!draft) return false;
    if (requiredFields.length === 0) {
        return Object.values(draft).some((v) => v !== null && v !== undefined && String(v).trim() !== '');
    }
    return requiredFields.some((f) => {
        const v = draft[f];
        return v !== null && v !== undefined && String(v).trim() !== '';
    });
}

export function saveDraft(slot, data, requiredFields = []) {
    if (!isMeaningful(data, requiredFields)) {
        clearDraft(slot);
        return false;
    }
    try {
        const payload = { data, savedAt: new Date().toISOString() };
        localStorage.setItem(keyFor(slot), JSON.stringify(payload));
        return true;
    } catch {
        return false;
    }
}

export function loadDraft(slot) {
    try {
        const raw = localStorage.getItem(keyFor(slot));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || !parsed.data) return null;
        return parsed; // { data, savedAt }
    } catch {
        return null;
    }
}

export function clearDraft(slot) {
    try {
        localStorage.removeItem(keyFor(slot));
    } catch {
        // ignore
    }
}

export function formatSavedAt(iso, locale = 'it-IT') {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffMs = now - d;
        const diffMin = Math.round(diffMs / 60000);
        if (diffMin < 1) return 'pochi secondi fa';
        if (diffMin < 60) return `${diffMin} min fa`;
        const diffH = Math.round(diffMin / 60);
        if (diffH < 24) return `${diffH} h fa`;
        return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
}
